import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { eventsAPI, venuesAPI, mediaAPI } from '../../services/api';
import { SAMPLE_TICKET_CATEGORIES } from '../../constants/ticketing';
import {
    FileText,
    MapPin,
    Settings,
    Ticket,
    LayoutDashboard,
    CheckCircle,
    ArrowLeft,
    ArrowRight,
} from 'lucide-react';
import './EventCreationWizard.css';

import BasicInfoStep from './steps/BasicInfoStep';
import VenueScheduleStep from './steps/VenueScheduleStep';
import EventDetailsStep from './steps/EventDetailsStep';
import TicketingStep from './steps/TicketingStep';
import ChartDesignStep from './steps/ChartDesignStep';
import ReviewStep from './steps/ReviewStep';

// Helper: YYYY-MM-DD N days from now (for sample data)
const getFutureDate = (daysFromNow) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString().split('T')[0];
};

// Helper: YYYY-MM-DDTHH:mm N days from now (for datetime-local inputs)
const getFutureDateTime = (daysFromNow, hours = 10) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    date.setHours(hours, 0, 0, 0);
    return date.toISOString().slice(0, 16);
};

// Pre-filled sample ticket types (outside component so it doesn't re-create on re-renders)
const SAMPLE_TICKET_TYPES = [
    {
        id: 1,
        category: 'Early Bird Special',
        code: 'EB',
        name: 'Early Bird General Admission',
        description: 'Early supporter discount. Includes admission and tea.',
        priceAmount: 45.0,
        capacity: 100,
        isSeated: true,
        currencyCode: 'USD',
        seatAssignment: 'OPEN_SEATING',
    },
    {
        id: 2,
        category: 'VIP Experience',
        code: 'VIP',
        name: 'VIP Premium Package',
        description: 'Premium seating, meet & greet, poster, appetizers, priority entry.',
        priceAmount: 125.0,
        capacity: 50,
        isSeated: true,
        currencyCode: 'USD',
        seatAssignment: 'ASSIGNED',
    },
    {
        id: 3,
        category: 'Standard',
        code: 'GA',
        name: 'General Admission',
        description: 'Standard admission with open seating.',
        priceAmount: 65.0,
        capacity: 300,
        isSeated: true,
        currencyCode: 'USD',
        seatAssignment: 'OPEN_SEATING',
    },
];

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

const EventCreationWizard = () => {
    const navigate = useNavigate();
    const { id: routeEventId } = useParams();

    // Editing mode: /events/:id vs /events/create
    const isEditing = routeEventId && routeEventId !== 'create';

    // Wizard UI state
    const [currentStep, setCurrentStep] = useState(1); // 1..6
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({}); // field -> message


    // Data sources
    const [venues, setVenues] = useState([]);
    const [ticketTypes, setTicketTypes] = useState([]);

    // Ticket form state (inside Tickets step)
    const [editingTicket, setEditingTicket] = useState(null);
    const [showTicketForm, setShowTicketForm] = useState(false);

    // Image upload UI state
    const [imageUploadState, setImageUploadState] = useState({
        previewUrl: '',
        isUploading: false,
        error: '',
        fileName: '',
        fileSize: 0,
    });

    // Seats.io / chart state (Step 5)
    const [chartCreating] = useState(false); // placeholder if you later add a “create chart” flow
    const [, setChartDesignComplete] = useState(false);

    // Logged-in user flags (admin-only UI)
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const isAdmin = !!(currentUser.isAdmin || currentUser.admin);

    // Main form state (single source of truth for payload)
    const [formData, setFormData] = useState({
        // Basic Information
        title: 'Persian Night: Classical Music Concert',
        description:
            'Join us for an enchanting evening celebrating Persian musical heritage. Experience traditional and contemporary Persian music performed by world-renowned artists with authentic instruments including tar, setar, and santur.',
        category: 'Concert',
        eventType: 'Live Performance',
        status: 'ACTIVE',

        // Venue & Schedule
        venueId: '',
        eventDate: getFutureDate(90),
        eventTime: '19:30',
        doorsOpenTime: '18:30',
        endDate: getFutureDate(90),
        endTime: '22:00',

        // Details
        ageRestriction: 'All Ages',
        dressCode: 'Smart Casual',
        specialInstructions:
            'Photography during intermission only. Arrive 30 min early for tea. Refreshments available.',

        // Revenue settings
        serviceFee: '',
        tax: '',

        // Ticket Sales (datetime-local)
        ticketSalesStart: getFutureDateTime(30),
        ticketSalesEnd: getFutureDateTime(89, 23),

        // Marketing
        imageUrl: '',
        isFeatured: true,
        tags: ['Persian', 'Classical', 'Traditional', 'Cultural', 'Music'],

        // Seating
        seatingMode: 'SEATED', // SEATED | MIXED | UNSEATED
        chartType: 'SIMPLE', // SIMPLE | ... (your backend enum)

        // Seats.io chart key (important for Step 5 + backend payload)
        seatsIoChartKey: '',

        // Planner assignment (admin can override by email)
        plannerEmail: currentUser?.email || '',

        // Chart duplication setting
        useSharedChart: false, // false = duplicate (default), true = use shared
        isNewChart: false, // track if we are creating a fresh chart (hides duplicate options)
    });

    // Ticket type form defaults
    const [ticketFormData, setTicketFormData] = useState({
        category: 'Student',
        code: 'STU',
        name: 'Student Admission',
        description: 'Student pricing with ID',
        priceAmount: 35.0,
        capacity: 75,
        isSeated: true,
        currencyCode: 'USD',
        seatAssignment: 'OPEN_SEATING',
        groupPackageAvailable: false,
        groupMinQuantity: '',
        groupUnitPrice: '',
    });

    // Step configuration (labels + icons)
    const steps = [
        { number: 1, label: 'Basic Info', icon: <FileText size={20} /> },
        { number: 2, label: 'Details', icon: <Settings size={20} /> },
        { number: 3, label: 'Tickets', icon: <Ticket size={20} /> },
        { number: 4, label: 'Venue Setup', icon: <MapPin size={20} /> },
        { number: 5, label: 'Chart Design', icon: <LayoutDashboard size={20} /> },
        { number: 6, label: 'Review', icon: <CheckCircle size={20} /> },
    ];

    // Seated events show Step 5; unseated skips it
    const isSeatedEvent = formData.seatingMode === 'SEATED' || formData.seatingMode === 'MIXED';
    const visibleSteps = steps.filter((s) => s.number !== 5 || isSeatedEvent);



    // Cleanup object URLs on unmount
    useEffect(() => {
        return () => {
            if (imageUploadState.previewUrl) URL.revokeObjectURL(imageUploadState.previewUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load venues + seed sample tickets when creating a new event
    useEffect(() => {
        const fetchVenues = async () => {
            try {
                const data = await venuesAPI.getAll();
                setVenues(data || []);

                // For new events, auto-select the first venue if available
                if (!isEditing && data && data.length > 0) {
                    setFormData((prev) => ({ ...prev, venueId: data[0].id }));
                }

                // For new events, preload sample ticket types + welcome toast
                if (!isEditing) {
                    setTicketTypes(SAMPLE_TICKET_TYPES);
                }
            } catch (err) {
                console.error('Failed to load venues', err);
            }
        };

        fetchVenues();
    }, [isEditing]);

    // Load existing event data in edit mode
    useEffect(() => {
        if (!isEditing) return;

        setLoading(true);

        const fetchData = async () => {
            try {
                const event = await eventsAPI.getById(routeEventId);

                const mappedData = {
                    title: event.title || '',
                    description: event.description || '',
                    category: event.category || 'Concert',
                    eventType: event.eventType || 'Live Performance',
                    status: event.status || 'ACTIVE',

                    venueId: event.venueId || '',
                    eventDate: event.startTime
                        ? new Date(event.startTime).toISOString().split('T')[0]
                        : event.eventDate || '',
                    eventTime: event.startTime
                        ? new Date(event.startTime).toTimeString().substring(0, 5)
                        : event.eventTime
                            ? event.eventTime.substring(0, 5)
                            : '',
                    doorsOpenTime: event.doorsOpenTime
                        ? event.doorsOpenTime.includes('T')
                            ? new Date(event.doorsOpenTime).toTimeString().substring(0, 5)
                            : event.doorsOpenTime.substring(0, 5)
                        : '',
                    endDate: event.endTime
                        ? new Date(event.endTime).toISOString().split('T')[0]
                        : event.endDate || '',
                    endTime: event.endTime
                        ? new Date(event.endTime).toTimeString().substring(0, 5)
                        : event.endTime
                            ? event.endTime.substring(0, 5)
                            : '',

                    ageRestriction: event.ageRestriction || 'All Ages',
                    dressCode: event.dressCode || 'Smart Casual',
                    specialInstructions: event.specialInstructions || '',

                    imageUrl: event.imageUrl || '',
                    isFeatured: !!event.isFeatured,
                    tags: event.tags || [],

                    serviceFee: event.serviceFee ?? '',
                    tax: event.tax ?? '',

                    seatingMode: event.seatingMode || 'SEATED',
                    chartType: event.chartConfig?.chartType || 'SIMPLE',
                    seatsIoChartKey: event.seatsIoChartKey || '',

                    plannerEmail: event.plannerEmail || '',
                };

                // datetime-local expects "YYYY-MM-DDTHH:mm"
                if (event.ticketSalesStart) {
                    mappedData.ticketSalesStart = new Date(event.ticketSalesStart).toISOString().slice(0, 16);
                }
                if (event.ticketSalesEnd) {
                    mappedData.ticketSalesEnd = new Date(event.ticketSalesEnd).toISOString().slice(0, 16);
                }

                setFormData(mappedData);

                // Keep chartKey state in sync so Step 5 works immediately
                // setChartKey(mappedData.seatsIoChartKey || event.chartId || '');

                // Load ticket types if backend sends them
                if (Array.isArray(event.ticketTypes) && event.ticketTypes.length > 0) {
                    setTicketTypes(event.ticketTypes);
                }
            } catch (err) {
                console.error('Failed to load event data', err);
                setErrors({ submit: 'Failed to load event. Please try refreshing.' });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isEditing, routeEventId]);

    // Generic form setter + error clearing
    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));

        // Keep chart state in sync with the form if the chart key is edited earlier
        // if (field === 'seatsIoChartKey') setChartKey(value);

        // Clear field-level errors as user fixes them
        if (errors[field]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    // Step validation (only blocks on truly required fields)
    const validateStep = (step) => {
        const newErrors = {};

        if (step === 1) {
            if (!formData.title?.trim()) newErrors.title = 'Title is required';
            if (!formData.category) newErrors.category = 'Category is required';
            if (!formData.eventType) newErrors.eventType = 'Event Type is required';
        }

        if (step === 3) {
            if (!formData.seatingMode) newErrors.seatingMode = 'Seating mode is required';
        }

        if (step === 4) {
            // Venue/Schedule/Seating are typically set in VenueScheduleStep
            if (!formData.venueId) newErrors.venueId = 'Please select a venue';

            if (!formData.eventDate) newErrors.eventDate = 'Event date is required';
            if (!formData.eventTime) newErrors.eventTime = 'Event start time is required';
            if (!formData.endDate) newErrors.endDate = 'End date is required';
            if (!formData.endTime) newErrors.endTime = 'End time is required';

            if (isSeatedEvent && !formData.chartType) newErrors.chartType = 'Chart type is required';
        }

        if (step === 5 && isSeatedEvent) {
            // If you require a chart key before review, enforce it here
            const key = (formData.seatsIoChartKey || '').trim();
            if (!key || key === 'new' || !formData.chartConfigured) {
                newErrors.seatsIoChartKey = 'Please wait for the chart to be fully initialized and configured.';
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setErrors(newErrors);
            return false;
        }

        return true;
    };

    const nextStep = () => {
        if (!validateStep(currentStep)) return;

        let next = currentStep + 1;

        // Skip Chart Design for UNSEATED events
        if (next === 5 && !isSeatedEvent) next = 6;

        setCurrentStep(next);
        window.scrollTo(0, 0);
    };

    const prevStep = () => {
        let prev = currentStep - 1;

        // Skip Chart Design backwards for UNSEATED events
        if (prev === 5 && !isSeatedEvent) prev = 4;

        setCurrentStep(prev);
        window.scrollTo(0, 0);
    };

    // Ticket helpers
    const isTicketCodeUnique = (code, excludeId = null) =>
        !ticketTypes.some((t) => t.code === code && t.id !== excludeId);

    const handleSaveTicketType = () => {
        const tErrors = {};

        if (!ticketFormData.name) tErrors.name = 'Name required';
        if (!ticketFormData.code) tErrors.code = 'Code required';
        else if (!isTicketCodeUnique(ticketFormData.code, editingTicket?.id)) {
            tErrors.code = 'Code must be unique';
        }

        if (Object.keys(tErrors).length > 0) {
            setErrors(tErrors);
            return;
        }

        if (editingTicket) {
            setTicketTypes((prev) =>
                prev.map((t) => (t.id === editingTicket.id ? { ...ticketFormData, id: t.id } : t))
            );
        } else {
            const newTicket = { ...ticketFormData, id: `temp-${Date.now()}` };
            setTicketTypes((prev) => [...prev, newTicket]);
        }

        setEditingTicket(null);
        setShowTicketForm(false);
        setErrors({});
    };

    const handleEditTicketType = (ticket) => {
        setEditingTicket(ticket);
        setTicketFormData(ticket);
        setShowTicketForm(true);
        setErrors({});
    };

    const handleDeleteTicketType = (id) => {
        setTicketTypes((prev) => prev.filter((t) => t.id !== id));
    };

    const resetTicketForm = () => {
        setEditingTicket(null);
        setShowTicketForm(false);
        setErrors({});
    };

    // Image upload handler (updates formData.imageUrl)
    const handleImageUpload = async (file) => {
        if (!file) return;

        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            setImageUploadState((prev) => ({ ...prev, error: 'File too large (max 5MB)' }));
            return;
        }

        // Revoke old preview URL to avoid memory leaks
        if (imageUploadState.previewUrl) URL.revokeObjectURL(imageUploadState.previewUrl);

        setImageUploadState((prev) => ({
            ...prev,
            isUploading: true,
            error: '',
            fileName: file.name,
            fileSize: file.size,
            previewUrl: URL.createObjectURL(file), // immediate preview while uploading
        }));

        try {
            const response = await mediaAPI.uploadEventImage(file);
            setFormData((prev) => ({ ...prev, imageUrl: response.data.url }));
            setImageUploadState((prev) => ({ ...prev, isUploading: false }));
        } catch (err) {
            console.error('Upload failed', err);
            let errorMessage = 'Upload failed';

            if (err.response) {
                // Handle Payload Too Large (413) specifically
                if (err.response.status === 413) {
                    errorMessage = 'File too large. Please upload a smaller file.';
                }
                // Handle specific backend error messages if available
                else if (err.response.data?.message) {
                    errorMessage = err.response.data.message;
                }
            } else if (err.message && err.message.includes('Network Error')) {
                errorMessage = 'Network error. Please check your connection.';
            }

            setImageUploadState((prev) => ({ ...prev, isUploading: false, error: errorMessage }));
        }
    };

    // Chart design completion callback
    const handleDesignComplete = (designData) => {
        console.log('Design saved', designData);
        setChartDesignComplete(true);
    };

    // Create/update submission
    const handleSubmit = async () => {
        setLoading(true);
        setErrors({});

        // Helper: parse datetime-local string into ISO
        const getISO = (dateStr, dateName) => {
            if (!dateStr) return null;
            const d = new Date(dateStr);
            if (Number.isNaN(d.getTime())) throw new Error(`Invalid Date Format: ${dateName}`);
            return d.toISOString();
        };

        // Helper: combine date + time into ISO
        const getCombinedISO = (dateP, timeP, fieldName) => {
            if (!dateP || !timeP) return null;
            const d = new Date(`${dateP}T${timeP}:00`);
            if (Number.isNaN(d.getTime())) throw new Error(`Invalid Date/Time: ${fieldName}`);
            return d.toISOString();
        };

        try {
            // Hard validation before payload
            if (!formData.eventDate || !formData.eventTime) {
                throw new Error('Event Start Date and Time are required.');
            }
            if (!formData.endDate || !formData.endTime) {
                throw new Error('Event End Date and Time are required.');
            }

            // If seated, ensure chart key is saved to payload
            const resolvedChartKey = (formData.seatsIoChartKey || '').trim();
            if (isSeatedEvent && !resolvedChartKey) {
                throw new Error('Seating chart is required for seated/mixed events.');
            }

            const startTimeISO = getCombinedISO(formData.eventDate, formData.eventTime, 'Start Time');
            const endTimeISO = getCombinedISO(formData.endDate, formData.endTime, 'End Time');
            const doorsOpenISO = formData.doorsOpenTime
                ? getCombinedISO(formData.eventDate, formData.doorsOpenTime, 'Doors Open Time')
                : null;

            const ticketStartISO = getISO(formData.ticketSalesStart, 'Ticket Sales Start');
            const ticketEndISO = getISO(formData.ticketSalesEnd, 'Ticket Sales End');

            const payload = {
                ...formData,
                // Ensure the saved chart key is consistent
                seatsIoChartKey: resolvedChartKey || formData.seatsIoChartKey,

                // Backend typically expects these timestamps as ISO strings
                startTime: startTimeISO,
                endTime: endTimeISO,
                doorsOpenTime: doorsOpenISO,
                ticketSalesStart: ticketStartISO,
                ticketSalesEnd: ticketEndISO,

                // Send ticket types; let backend generate IDs for temp/new ones
                ticketTypes: ticketTypes.map((t) => {
                    const isTempId = String(t.id).startsWith('temp-') || typeof t.id === 'number';
                    return {
                        ...t,
                        id: isTempId ? null : t.id,
                        priceAmount: Number.parseFloat(t.priceAmount),
                        capacity: Number.parseInt(t.capacity, 10),
                        seatAssignment: t.seatAssignment || 'OPEN_SEATING',
                        groupPackageAvailable: !!t.groupPackageAvailable,
                        groupMinQuantity: t.groupMinQuantity !== '' ? Number.parseInt(t.groupMinQuantity, 10) : null,
                        groupUnitPrice: t.groupUnitPrice !== '' ? Number.parseFloat(t.groupUnitPrice) : null,
                    };
                }),
            };

            if (isEditing) {
                await eventsAPI.update(routeEventId, payload);
            } else {
                await eventsAPI.create(payload);
            }

            setTimeout(() => navigate('/dashboard/events'), 1000);
        } catch (err) {
            console.error('Submit failed', err);
            const msg = err?.message || 'Failed to save event. Please check all fields.';
            setErrors({ submit: msg });
        } finally {
            setLoading(false);
        }
    };

    // Step renderer (kept consistent with the steps[] order)
    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <BasicInfoStep
                        formData={formData}
                        errors={errors}
                        eventCategories={['Concert', 'Conference', 'Theater', 'Sports', 'Other']}
                        eventTypes={['Live Performance', 'Workshop', 'Networking', 'Tournament']}
                        handleInputChange={handleInputChange}
                        isAdmin={isAdmin}
                    />
                );

            case 2:
                return (
                    <EventDetailsStep
                        formData={formData}
                        errors={errors}
                        handleInputChange={handleInputChange}
                        onImageUpload={handleImageUpload}
                        imageUploadState={imageUploadState}
                    />
                );

            case 3:
                return (
                    <TicketingStep
                        ticketTypes={ticketTypes}
                        ticketFormData={ticketFormData}
                        showTicketForm={showTicketForm}
                        editingTicket={editingTicket}
                        errors={errors}
                        sampleTicketCategories={SAMPLE_TICKET_CATEGORIES || []}
                        formData={formData}
                        handleInputChange={handleInputChange}
                        onToggleTicketForm={() => {
                            setShowTicketForm((v) => !v);
                            setErrors({});
                        }}
                        onTicketFormChange={(field, value) =>
                            setTicketFormData((prev) => ({ ...prev, [field]: value }))
                        }
                        onAddTicketType={handleSaveTicketType}
                        onEditTicketType={handleEditTicketType}
                        onDeleteTicketType={handleDeleteTicketType}
                        onCancelTicketForm={resetTicketForm}
                    />
                );

            case 4:
                return (
                    <VenueScheduleStep
                        formData={formData}
                        errors={errors}
                        venues={venues}
                        handleInputChange={handleInputChange}
                    />
                );

            case 5:
                return (
                    <ChartDesignStep
                        formData={formData}
                        chartKey={formData.seatsIoChartKey}
                        chartCreating={chartCreating}
                        ticketTypes={ticketTypes}
                        handleInputChange={handleInputChange}
                        onDesignComplete={handleDesignComplete}
                        onCancelDesign={() => setCurrentStep(4)}
                    />
                );

            case 6:
                return (
                    <ReviewStep formData={formData} venues={venues} ticketTypes={ticketTypes} errors={errors} />
                );

            default:
                return null;
        }
    };

    return (
        <div className="wizard-container">
            <Helmet>
                <title>{isEditing ? 'Edit Event' : 'Create Event'} | TikPlat</title>
            </Helmet>

            <div className="wizard-content">
                <header className="wizard-header">
                    <h1>{isEditing ? 'Edit Event' : 'Create New Event'}</h1>
                    <p>
                        {isEditing
                            ? 'Update your event details and manage settings.'
                            : 'Follow the simple steps below to set up your event page and start selling tickets!'}
                    </p>
                </header>



                {errors.submit && <div className="error-message">{errors.submit}</div>}

                <div className="step-indicator">
                    {visibleSteps.map((step) => (
                        <div
                            key={step.number}
                            className={`step ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''
                                }`}
                        >
                            <div className="step-icon-wrapper">{step.icon}</div>
                            <div className="step-label">{step.label}</div>
                        </div>
                    ))}
                </div>

                {renderStepContent()}

                <div className="action-buttons">
                    <button className="back-btn" onClick={prevStep} disabled={currentStep === 1 || loading}>
                        <ArrowLeft size={18} />
                        Back
                    </button>

                    {currentStep < steps.length ? (
                        <button className="next-btn" onClick={nextStep} disabled={loading}>
                            {/* If Step 4 is the last step before Review (unseated), show “Review” */}
                            {currentStep === 4 && !isSeatedEvent ? 'Review' : 'Next Step'}
                            <ArrowRight size={18} />
                        </button>
                    ) : (
                        <button className="next-btn" onClick={handleSubmit} disabled={loading}>
                            {loading ? (
                                <>
                                    <div className="spinner" /> {isEditing ? 'Updating...' : 'Publishing...'}
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={18} />
                                    {isEditing ? 'Update Event' : 'Publish Event'}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EventCreationWizard;
